import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import Button from './Button';
import Panel from './Panel';

interface TitledPanelProps {
  children?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
  id?: string;
  size?: 'sm' | 'md';
  /** When set, renders an XIcon in the top-right of the title row. */
  onClose?: () => void;
  closeAriaLabel?: string;
  /**
   * Stays below the body and does not scroll with it. Providing a footer makes
   * the body the scroll region, full width of the card, so the scrollbar sits
   * on the panel edge.
   */
  footer?: ReactNode;
  /** Extra classes for the scrolling body. Only used when the body scrolls. */
  bodyClassName?: string;
  /** Scroll the body even when there is no footer. */
  scrollable?: boolean;
}

/** 1px rules at the clip edges when the body can still scroll that way. */
function ScrollEdges({ className, children }: { className?: string; children?: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ above: false, below: false });

  const updateEdges = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const above = scroller.scrollTop > 1;
    const below = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop > 1;
    setEdges((current) => (current.above === above && current.below === below ? current : { above, below }));
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    updateEdges();
    scroller.addEventListener('scroll', updateEdges, { passive: true });
    const observer = new ResizeObserver(updateEdges);
    observer.observe(scroller);
    for (const child of scroller.children) observer.observe(child);
    const mutations = new MutationObserver(updateEdges);
    mutations.observe(scroller, { childList: true, subtree: true, characterData: true });
    return () => {
      scroller.removeEventListener('scroll', updateEdges);
      observer.disconnect();
      mutations.disconnect();
    };
  }, [updateEdges]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {edges.above && (
        <div className="pointer-events-none absolute top-0 z-[1] h-px -translate-y-full bg-border-default left-[calc(1rem-0.25rem)] right-[calc(1.125rem-17px)] md:left-[calc(1.5rem-0.25rem)] md:right-[calc(1.625rem-17px)]" />
      )}
      <div ref={scrollerRef} tabIndex={0} onScroll={updateEdges} className={className}>
        {children}
      </div>
      {edges.below && (
        <div className="pointer-events-none absolute bottom-0 z-[1] h-px translate-y-full bg-border-default left-[calc(1rem-0.25rem)] right-[calc(1.125rem-17px)] md:left-[calc(1.5rem-0.25rem)] md:right-[calc(1.625rem-17px)]" />
      )}
    </div>
  );
}

/** Panel with a title row, optional subtitle, and optional header actions. */
export default function TitledPanel({
  children,
  title,
  subtitle,
  actions,
  className,
  id,
  size = 'md',
  onClose,
  closeAriaLabel = 'Close',
  footer,
  bodyClassName,
  scrollable = false
}: TitledPanelProps) {
  const isSm = size === 'sm';
  const header = (
    <div className={twMerge('flex flex-wrap items-start justify-between', isSm ? 'gap-2' : 'gap-3')}>
      <div className="min-w-0">
        {typeof title === 'string' ? (
          <h3 className={twMerge('font-semibold text-content-default', isSm ? 'text-base' : 'text-lg')}>{title}</h3>
        ) : (
          title
        )}
        {subtitle != null && <p className="text-xs text-content-subtle">{subtitle}</p>}
      </div>
      {(actions != null || onClose) && (
        <div className="flex items-center gap-2 ml-auto">
          {actions}
          {onClose != null && (
            <Button icon={XIcon} variant="ghost" size="sm" aria-label={closeAriaLabel} onClick={onClose} />
          )}
        </div>
      )}
    </div>
  );

  if (footer == null && !scrollable) {
    return (
      <Panel id={id} size={size} className={twMerge(isSm && 'gap-2', className)}>
        {header}
        {children}
      </Panel>
    );
  }

  return (
    <Panel id={id} size={size} className={twMerge('px-0 md:px-0', isSm && 'gap-2', className)}>
      <div className="px-4 md:px-6">{header}</div>
      <ScrollEdges
        className={twMerge(
          'custom-scrollbar-thin max-h-96 min-h-0 overflow-y-auto pl-4 pr-2.5 md:pl-6 mr-[calc(1.125rem-17px)] md:mr-[calc(1.625rem-17px)]',
          bodyClassName
        )}
      >
        {children}
      </ScrollEdges>
      {footer != null && <div className="px-4 md:px-6">{footer}</div>}
    </Panel>
  );
}
